import { useMemo, useState } from "react";
import type { Project } from "@/features/leaderboard/types/index.ts";
import { useScoreFundingScatter } from "../hooks/use-intelligence-queries.ts";
import { CATEGORY_FAMILIES, type CategoryFamily, type ScatterPoint } from "../types/index.ts";
import { ProjectNameLink } from "./ProjectNameLink.tsx";

const WIDTH = 760;
const HEIGHT = 420;
const PADDING = { top: 16, right: 24, bottom: 36, left: 56 };

// One color per family. Matches categories.color palette where
// possible; otherwise derived.
const FAMILY_COLORS: Record<CategoryFamily, string> = {
  DeFi: "#8b5cf6",
  Infra: "#f59e0b",
  "AI & DePIN": "#06b6d4",
  Payments: "#0f766e",
  "Tools & Exchange": "#14b8a6",
  "Gaming & NFT": "#ec4899",
  "Prediction & Betting": "#e9d5ff",
  "Privacy & Identity": "#475569",
  SocialFi: "#3b82f6",
  RWA: "#3730a3",
  Other: "#6b7280",
};

function formatDollars(n: number): string {
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

interface ScoreFundingScatterProps {
  onOpenProjectDetail?: (project: Project) => void;
}

export function ScoreFundingScatter({ onOpenProjectDetail }: ScoreFundingScatterProps = {}) {
  const { data, isLoading, error } = useScoreFundingScatter();
  const [activeFamilies, setActiveFamilies] = useState<Set<CategoryFamily>>(
    new Set(CATEGORY_FAMILIES)
  );
  const [hoveredPoint, setHoveredPoint] = useState<ScatterPoint | null>(null);

  const plot = useMemo(() => {
    if (!data || data.points.length === 0) return null;
    const scoreMin = 0;
    const scoreMax = 100;
    const logFunds = data.points.map((p) => Math.log(p.fundingRaisedUsd));
    const logMin = Math.min(...logFunds);
    const logMax = Math.max(...logFunds);
    const xRange = scoreMax - scoreMin;
    const yRange = logMax - logMin || 1;
    const plotW = WIDTH - PADDING.left - PADDING.right;
    const plotH = HEIGHT - PADDING.top - PADDING.bottom;
    const xScale = (score: number) => PADDING.left + ((score - scoreMin) / xRange) * plotW;
    const yScale = (logF: number) => PADDING.top + plotH - ((logF - logMin) / yRange) * plotH;

    // Regression line: LN(funding) = slope * score + intercept
    // Draw between score=0 and score=100
    const regStart = { x: xScale(0), y: yScale(data.regressionIntercept) };
    const regEnd = {
      x: xScale(100),
      y: yScale(data.regressionSlope * 100 + data.regressionIntercept),
    };

    // Y-axis ticks at $100k, $1M, $10M, $100M, $1B
    const yTicks = [1e5, 1e6, 1e7, 1e8, 1e9]
      .filter((v) => Math.log(v) >= logMin - 0.5 && Math.log(v) <= logMax + 0.5)
      .map((v) => ({ value: v, y: yScale(Math.log(v)) }));

    return { xScale, yScale, regStart, regEnd, yTicks };
  }, [data]);

  if (isLoading) return <div className="intel-loading">Loading scatter…</div>;
  if (error) return <div className="intel-error">Failed to load scatter</div>;
  if (!data) return <div className="intel-error">Failed to load scatter</div>;
  // Empty-state: backend returns `{ points: [], slope: 0, intercept: 0 }`
  // when the qualifying dataset has < 2 rows (degenerate regression).
  // Render an explanatory message instead of a scary error.
  if (!plot || data.points.length === 0) {
    return (
      <section className="intel-section">
        <header className="intel-section__header">
          <h3 className="intel-section__title">Score × funding scatter</h3>
        </header>
        <div className="intel-loading">
          Not enough funded projects (≥&nbsp;$100k) to fit a regression yet.
        </div>
      </section>
    );
  }

  const visiblePoints = data.points.filter((p) => activeFamilies.has(p.family));

  // Top-10 bargains (most negative residual) for the sidebar list
  const bargains = [...data.points].sort((a, b) => a.residual - b.residual).slice(0, 10);

  return (
    <section className="intel-section">
      <header className="intel-section__header">
        <h3 className="intel-section__title">Score × funding scatter</h3>
        <p className="intel-section__subtitle">
          Each dot is one project. The regression line fits log(funding) ~ alpha score — projects
          below the line raised less than their score would predict (bargain candidates). Axis is
          log-scale because raises span 5 orders of magnitude.
        </p>
      </header>

      <div className="intel-scatter-layout">
        <div className="intel-scatter-plot">
          <svg
            width={WIDTH}
            height={HEIGHT}
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            role="img"
            aria-label="Score versus funding scatter plot with regression line"
          >
            {/* Y gridlines + axis labels */}
            {plot.yTicks.map((t) => (
              <g key={t.value}>
                <line
                  x1={PADDING.left}
                  x2={WIDTH - PADDING.right}
                  y1={t.y}
                  y2={t.y}
                  stroke="#334155"
                  strokeWidth={0.5}
                  strokeDasharray="2,2"
                />
                <text
                  x={PADDING.left - 6}
                  y={t.y + 3}
                  fontSize={10}
                  fill="#94a3b8"
                  textAnchor="end"
                >
                  {formatDollars(t.value)}
                </text>
              </g>
            ))}
            {/* X axis ticks */}
            {[0, 20, 40, 60, 80, 100].map((score) => (
              <g key={score}>
                <text
                  x={plot.xScale(score)}
                  y={HEIGHT - PADDING.bottom + 16}
                  fontSize={10}
                  fill="#94a3b8"
                  textAnchor="middle"
                >
                  {score}
                </text>
              </g>
            ))}
            <text x={WIDTH / 2} y={HEIGHT - 6} fontSize={11} fill="#cbd5e1" textAnchor="middle">
              Alpha Score
            </text>
            {/* Regression line */}
            <line
              x1={plot.regStart.x}
              x2={plot.regEnd.x}
              y1={plot.regStart.y}
              y2={plot.regEnd.y}
              stroke="#eab308"
              strokeWidth={1.5}
              strokeDasharray="4,3"
              opacity={0.8}
            />
            {/* Data points */}
            {visiblePoints.map((p) => (
              // biome-ignore lint/a11y/useSemanticElements: SVG children must be SVG elements, <button> is not valid inside <svg>
              <circle
                key={p.projectId}
                role="button"
                aria-label={`${p.name}: score ${p.alphaScore.toFixed(1)}, raised ${formatDollars(p.fundingRaisedUsd)}`}
                cx={plot.xScale(p.alphaScore)}
                cy={plot.yScale(Math.log(p.fundingRaisedUsd))}
                r={p === hoveredPoint ? 5 : 3}
                fill={FAMILY_COLORS[p.family]}
                opacity={p === hoveredPoint ? 1 : 0.75}
                stroke={p === hoveredPoint ? "#fff" : "none"}
                strokeWidth={1}
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setHoveredPoint(p)}
                onMouseLeave={() => setHoveredPoint(null)}
              />
            ))}
          </svg>

          {hoveredPoint && (
            <div className="intel-scatter-tooltip">
              <strong>{hoveredPoint.name}</strong>
              <div>Score: {hoveredPoint.alphaScore.toFixed(1)}</div>
              <div>Raised: {formatDollars(hoveredPoint.fundingRaisedUsd)}</div>
              <div>Family: {hoveredPoint.family}</div>
              <div>
                Residual: {hoveredPoint.residual > 0 ? "+" : ""}
                {hoveredPoint.residual.toFixed(2)}
              </div>
            </div>
          )}

          <div className="intel-scatter-legend">
            {CATEGORY_FAMILIES.map((family) => (
              <button
                type="button"
                key={family}
                className={`intel-legend-pill${activeFamilies.has(family) ? " active" : ""}`}
                onClick={() => {
                  const next = new Set(activeFamilies);
                  if (next.has(family)) next.delete(family);
                  else next.add(family);
                  setActiveFamilies(next);
                }}
              >
                <span
                  className="intel-legend-swatch"
                  style={{ background: FAMILY_COLORS[family] }}
                />
                {family}
              </button>
            ))}
          </div>
        </div>

        <aside className="intel-bargain-list">
          <h4>Top 10 bargains (all families)</h4>
          <p className="intel-bargain-list__hint">
            Most negative residual = raised less than score would predict. The list ignores the
            legend filter — always shows the global top 10.
          </p>
          <ol>
            {bargains.map((p) => (
              <li key={p.projectId} className="intel-bargain-list__item">
                <div className="intel-bargain-list__name">
                  <ProjectNameLink
                    projectId={p.projectId}
                    label={p.name}
                    onOpenProjectDetail={onOpenProjectDetail}
                  />
                </div>
                <div className="intel-bargain-list__meta">
                  score {p.alphaScore.toFixed(1)} · raised {formatDollars(p.fundingRaisedUsd)} ·{" "}
                  <span className="intel-bargain-list__family">{p.family}</span>
                </div>
              </li>
            ))}
          </ol>
        </aside>
      </div>
    </section>
  );
}
